import { useEffect, useState } from 'react';

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  photo_url?: string;
}

interface UseTelegramReturn {
  user: TelegramUser | null;
  initDataRaw: string;
  isReady: boolean;
  isInTelegram: boolean;
  themeParams: any;
  haptic: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
}

export function useTelegram(): UseTelegramReturn {
  const [isReady, setIsReady] = useState(false);
  const [isInTelegram, setIsInTelegram] = useState(false);
  const [user, setUser] = useState<TelegramUser | null>(null);
  const [initDataRaw, setInitDataRaw] = useState('');

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    
    if (tg) {
      tg.ready();
      tg.expand();
      
      setUser(tg.initDataUnsafe?.user || null);
      setInitDataRaw(tg.initData || '');
      setIsReady(true);
      setIsInTelegram(true);

      // Для разработки - если нет данных Telegram
      if (!tg.initDataUnsafe?.user && import.meta.env.DEV) {
        const mockUser: TelegramUser = {
          id: 123456789,
          first_name: 'Dev',
          last_name: 'User',
          username: 'devuser',
          language_code: 'ru',
        };
        setUser(mockUser);
        setInitDataRaw('mock_init_data');
      }
    } else {
      // Разработка без Telegram
      if (import.meta.env.DEV) {
        const mockUser: TelegramUser = {
          id: 123456789,
          first_name: 'Dev',
          last_name: 'User',
          username: 'devuser',
          language_code: 'ru',
        };
        setUser(mockUser);
        setInitDataRaw('mock_init_data');
        setIsReady(true);
      }
    }
  }, []);

  const haptic = {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => {
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred(style);
    },
    notificationOccurred: (type: 'error' | 'success' | 'warning') => {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred(type);
    },
    selectionChanged: () => {
      window.Telegram?.WebApp?.HapticFeedback?.selectionChanged();
    },
  };

  return {
    user,
    initDataRaw,
    isReady,
    isInTelegram,
    themeParams: window.Telegram?.WebApp?.themeParams || {},
    haptic,
  };
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe: {
          user?: TelegramUser;
          query_id?: string;
          auth_date?: number;
          hash?: string;
        };
        themeParams: any;
        backgroundColor: string;
        ready: () => void;
        expand: () => void;
        close: () => void;
        enableClosingConfirmation: () => void;
        disableClosingConfirmation: () => void;
        HapticFeedback?: {
          impactOccurred: (style: string) => void;
          notificationOccurred: (type: string) => void;
          selectionChanged: () => void;
        };
      };
    };
  }
}
